import { createAsyncThunk } from "@reduxjs/toolkit";

import __ from "web/Layout/Translations";

import { IApiRequestMethods } from "web/api/apiRequestTypes";

import parseObjectToGetParameters from "web/utils/data/parser/object/parseObjectToGetParameters";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import restUrls from "web/constants/restUrls";

import type { ICodes } from "web/types/Code";
import type { NumberBoolean } from "web/types/Utils";

import { request } from "web/api";
import { setNotificationSuccess } from "web/features/app/appSlice";
import {
  hydrateCodesFromCache,
  revertCodeUsed,
  setCodeUsedRequest,
} from "web/features/codes/codesSlice";
import { AppDispatch, RootState } from "web/store";

import { FilterValues, getFilters } from "./utils/helpers";

interface IGetCodesArgs {
  page?: number;
  pageSize?: number;
  filterValue?: FilterValues;
  searchName?: string;
}

const buildCacheKey = (args: IGetCodesArgs, state: RootState) => {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 12;
  const filterValue = args.filterValue ?? FilterValues.ALL;
  const searchName = args.searchName?.trim() ?? "";
  const itemsFromState = state.codes?.items || [];
  /**
   * if user enters page > 1 from a direct link we should
   * fetch also items from previous pages
   */
  const shouldFetchPreviousItems =
    page > 1 && itemsFromState.length !== (page - 1) * pageSize;

  return {
    page,
    pageSize,
    filterValue,
    searchName,
    shouldFetchPreviousItems,
    itemsFromState,
    cacheKey: JSON.stringify({
      page,
      pageSize,
      filterValue,
      searchName,
      shouldFetchPreviousItems,
    }),
  };
};

export const fetchCodes = createAsyncThunk(
  "codes/fetchCodes",
  async (args: IGetCodesArgs, { getState }) => {
    const {
      page,
      pageSize,
      filterValue,
      searchName,
      shouldFetchPreviousItems,
      itemsFromState,
      cacheKey,
    } = buildCacheKey(args, getState() as RootState);

    const filtersBase = getFilters(filterValue) ?? [];
    const filters = searchName
      ? [
          ...filtersBase,
          {
            filters: [
              {
                field: "name",
                value: `%25${searchName}%25`,
                conditionType: "like",
              },
            ],
          },
        ]
      : filtersBase;

    const sortOrders = [
      {
        field: "created_at",
        direction: "desc",
      },
    ];

    const paramsString = parseObjectToGetParameters({
      searchCriteria: {
        filterGroups: filters,
        pageSize: shouldFetchPreviousItems ? page * pageSize : pageSize,
        currentPage: shouldFetchPreviousItems ? 1 : page,
        sortOrders,
      },
    });

    const response = (await request(
      `${restUrls.customerCodes}?${paramsString}`
    )) as ICodes;

    const codeItems =
      response && isArrayHasItems(response.items) ? response.items : [];
    const {
      to_use_count: toUseCount,
      total_count: totalCount,
      used_count: usedCount,
      canceled_count: canceledCount,
    } = response || {};

    const items =
      page > 1 && !shouldFetchPreviousItems
        ? [...itemsFromState, ...codeItems]
        : codeItems;

    return {
      codes: items,
      toUseCount,
      usedCount,
      canceledCount,
      totalCount,
      cacheKey,
    };
  }
);

export const getCodes =
  (args: IGetCodesArgs = {}) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { cacheKey } = buildCacheKey(args, getState());
    const cached = getState().codes.cache[cacheKey];

    if (cached) {
      return dispatch(hydrateCodesFromCache(cached));
    }

    return dispatch(fetchCodes(args));
  };

export const setCodeUsed = createAsyncThunk(
  "codes/setCodeUsed",
  async (
    payload: { itemId: number; isUsed: NumberBoolean },
    { getState, dispatch }
  ) => {
    const { itemId, isUsed } = payload;
    const { codes } = getState() as RootState;
    const itemsIdsMarkedAsToUseInitial = codes
      ? codes.itemsIdsMarkedAsToUse
      : [];
    const itemsIdsMarkedAsUsedInitial = isArrayHasItems(
      codes?.itemsIdsMarkedAsUsed
    )
      ? codes.itemsIdsMarkedAsUsed
      : [];
    const settingMarkIds =
      codes && codes.settingMarkIds ? codes.settingMarkIds : [];
    const newState = isUsed
      ? {
          toUse: itemsIdsMarkedAsToUseInitial.filter((id) => id !== itemId),
          used: [...itemsIdsMarkedAsUsedInitial, itemId],
        }
      : {
          toUse: [...itemsIdsMarkedAsToUseInitial, itemId],
          used: itemsIdsMarkedAsUsedInitial.filter((id) => id !== itemId),
        };

    await dispatch(
      setCodeUsedRequest({
        ...newState,
        mark: [...settingMarkIds, itemId],
      })
    );
    try {
      await request(
        restUrls.customerCodesMark.replace(":itemId", itemId.toString()),
        {
          method: IApiRequestMethods.PUT,
          body: JSON.stringify({
            used: isUsed,
          }),
        }
      );

      const { codes: codesState } = getState() as RootState;
      const settingMarkIdsState = isArrayHasItems(codesState?.settingMarkIds)
        ? codesState.settingMarkIds
        : [];
      const settingMarkIdsFiltered = isArrayHasItems(settingMarkIdsState)
        ? settingMarkIdsState.filter((id) => id !== itemId)
        : [];
      dispatch(
        setNotificationSuccess({
          message: isUsed
            ? __("Kod oznaczono jako wykorzystany")
            : __("Kod oznaczono jako niewykorzystany"),
        })
      );
      return { mark: settingMarkIdsFiltered };
    } catch (error) {
      const { codes: codesState } = getState() as RootState;
      const settingMarkIdsState =
        codesState && codesState.settingMarkIds
          ? codesState.settingMarkIds
          : [];
      const settingMarkIdsFiltered = isArrayHasItems(settingMarkIdsState)
        ? settingMarkIdsState.filter((id) => id !== itemId)
        : [];
      dispatch(
        revertCodeUsed({
          mark: settingMarkIdsFiltered,
        })
      );

      throw error;
    }
  }
);
